import { createStore } from 'zustand/vanilla';
import { workingViewStore } from './workingViewStore';

/**
 * #1078 — the rolling list behind Game Detail's Recent views menu.
 *
 * Page lifetime, like the working view it returns into: no storage, cleared by
 * a reload. Each entry carries the working-view scope and epoch it was written
 * under, so a logout, identity or grant change (which advances that epoch)
 * hides every older entry. Labels and a route only — never market, account or
 * execution data.
 */
export interface RecentGameView {
  /** The game's route identity (Game Detail's `oddsKey`); one entry per game. */
  gameKey: string;
  /** The game route without its query: a Recent return never replays a focus link. */
  path: string;
  title: string;
  subtitle: string;
  /** The keys Game Detail's sections read their working view under, so the
   *  menu can describe the view a Recent return resumes. */
  sectionKeys: { event: string | null; game: string | null };
}
interface Entry extends RecentGameView { scope: string; epoch: number }

export const MAX_RECENT_GAME_VIEWS = 8;
export const recentGameViewsStore = createStore<{ entries: readonly Entry[] }>(() => ({ entries: [] }));

const sameView = (a: RecentGameView, b: RecentGameView) =>
  a.gameKey === b.gameKey && a.path === b.path && a.title === b.title && a.subtitle === b.subtitle
  && a.sectionKeys.event === b.sectionKeys.event && a.sectionKeys.game === b.sectionKeys.game;

export function visibleRecentGameViews(
  entries: readonly Entry[], scope: string | null, epoch: number,
): readonly Entry[] {
  return entries.filter((e) => e.scope === scope && e.epoch === epoch);
}

/** Most recent first, deduplicated by game, capped. Without a scope, nothing is kept. */
export function noteRecentGameView(view: RecentGameView): void {
  const { scope, epoch } = workingViewStore.getState();
  if (!scope) return;
  const visible = visibleRecentGameViews(recentGameViewsStore.getState().entries, scope, epoch);
  if (visible[0] && sameView(visible[0], view)) return;
  recentGameViewsStore.setState({
    entries: [{ ...view, scope, epoch }, ...visible.filter((e) => e.gameKey !== view.gameKey)]
      .slice(0, MAX_RECENT_GAME_VIEWS),
  });
}
